import React, { useState, useEffect, useRef } from "react";

/**
 * Lazy-loaded Google Sign-In button
 * Loads script asynchronously without blocking the page
 */
function LazyGoogleButton({ googleResponse }) {
  const [isAvailable, setIsAvailable] = useState(false);
  const buttonRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const loadGoogleGSI = () => {
      console.log("Starting Google Sign-In load...");
      
      // Check if already loaded
      if (window.google && buttonRef.current) {
        console.log("Google already loaded, rendering button");
        const clientId = window.__GOOGLE_CLIENT_ID__ || "1013016136996-gbsgi5u4co0meo5m2dmof85sdgg6cgk3.apps.googleusercontent.com";
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: googleResponse
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: "outline",
          size: "large",
          text: "signin_with",
          logo_alignment: "center",
          locale: "fa_IR"
        });
        setIsAvailable(true);
        return;
      }

      // Set timeout - hide button if Google doesn't load in 10 seconds
      timeoutRef.current = setTimeout(() => {
        if (mounted) {
          console.log("Google Sign-In timeout after 10 seconds");
          setIsAvailable(false);
        }
      }, 10000); // 10 seconds for slow connections/VPN

      // Load the script
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      
      console.log("Adding Google script to DOM...");
      
      script.onload = () => {
        console.log("Google script loaded successfully");
        if (!mounted) return;
        clearTimeout(timeoutRef.current);
        
        // Initialize Google Sign-In when ready
        if (window.google && buttonRef.current) {
          console.log("Initializing and rendering Google button");
          const clientId = window.__GOOGLE_CLIENT_ID__ || "1013016136996-gbsgi5u4co0meo5m2dmof85sdgg6cgk3.apps.googleusercontent.com";
          
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: googleResponse
          });
          
          window.google.accounts.id.renderButton(
            buttonRef.current,
            {
              theme: "outline",
              size: "large",
              text: "signin_with",
              logo_alignment: "center",
              locale: "fa_IR"
            }
          );
          
          setIsAvailable(true);
          console.log("Google button rendered successfully");
        } else {
          console.warn("window.google or buttonRef not available after load");
        }
      };
      
      script.onerror = (error) => {
        console.error("Google script failed to load:", error);
        if (mounted) {
          clearTimeout(timeoutRef.current);
          setIsAvailable(false);
        }
      };
      
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (!existingScript) {
        document.head.appendChild(script);
        console.log("Script appended to head");
      } else {
        console.log("Script already exists in DOM");
        // Script exists, check if Google is ready
        clearTimeout(timeoutRef.current);
        if (window.google && buttonRef.current) {
          console.log("Google already available, rendering now");
          const clientId = window.__GOOGLE_CLIENT_ID__ || "1013016136996-gbsgi5u4co0meo5m2dmof85sdgg6cgk3.apps.googleusercontent.com";
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: googleResponse
          });
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: "outline",
            size: "large",
            text: "signin_with",
            logo_alignment: "center",
            locale: "fa_IR"
          });
          setIsAvailable(true);
        } else {
          console.log("Waiting for Google to become available...");
          // Wait a bit for Google to initialize
          setTimeout(() => {
            if (window.google && buttonRef.current && mounted) {
              console.log("Google now available after wait");
              const clientId = window.__GOOGLE_CLIENT_ID__ || "1013016136996-gbsgi5u4co0meo5m2dmof85sdgg6cgk3.apps.googleusercontent.com";
              window.google.accounts.id.initialize({
                client_id: clientId,
                callback: googleResponse
              });
              window.google.accounts.id.renderButton(buttonRef.current, {
                theme: "outline",
                size: "large",
                text: "signin_with",
                logo_alignment: "center",
                locale: "fa_IR"
              });
              setIsAvailable(true);
            }
          }, 500);
        }
      }
    };

    loadGoogleGSI();

    return () => {
      mounted = false;
      clearTimeout(timeoutRef.current);
    };
  }, [googleResponse]);

  // Always show the container, it will be populated when Google loads
  return (
    <div className="send-or-register">
      <div ref={buttonRef}></div>
      {!isAvailable && <div style={{minHeight: '40px'}}></div>}
    </div>
  );
}

export default LazyGoogleButton;
